import PropTypes from "prop-types";
import { useCallback, useState } from "react";
import useSWR from "swr";
import fetcher from "../../../hooks/fetcher";
import { useCurrentUser } from "../../../globalState";
import AuthModal from "../../../components/AuthModal";
import { HeartIcon } from "./Icons.jsx";
import s from "./FavouriteButton.module.css";

const buildFavouriteBody = (isFavourite, influencerId, productId) => ({
  is_favourite: isFavourite,
  ...(influencerId ? { influencer_id: influencerId } : {}),
  ...(productId ? { product_id: productId } : {}),
});

function useFavouriteStatus(influencerId, productId) {
  const route = influencerId ? "" : "product/";
  const id = influencerId || productId;
  const { data, error, mutate } = useSWR(
    id ? `/api/favourites/${route}${id}` : null,
    fetcher,
  );

  return {
    isFavourite: data?.isFavourite ?? false,
    isLoading: !data && !error,
    refresh: mutate,
  };
}

export default function FavouriteButton({
  influencerId,
  productId,
  variant = "light",
}) {
  const currentUser = useCurrentUser();
  const { isFavourite, refresh } = useFavouriteStatus(
    currentUser.email ? influencerId || productId : null,
  );
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isToggling, setIsToggling] = useState(false);

  const onToggle = useCallback(async () => {
    if (!currentUser.email) {
      setShowAuthModal(true);
      return;
    }
    if (isToggling) return;

    const newValue = !isFavourite;
    setIsToggling(true);

    // Optimistic: update SWR cache immediately without revalidation
    refresh({ isFavourite: newValue }, false);

    try {
      await fetcher("/api/favourites/status", {
        method: "POST",
        body: JSON.stringify(
          buildFavouriteBody(newValue, influencerId, productId),
        ),
      });
      await refresh(); // confirm from server
    } catch (err) {
      console.error(err);
      await refresh(); // revert to server truth
    } finally {
      setIsToggling(false);
    }
  }, [
    currentUser.email,
    influencerId,
    productId,
    isFavourite,
    refresh,
    isToggling,
  ]);

  const handleCloseAuthModal = useCallback(
    async (user) => {
      setShowAuthModal(false);
      if (!user) return;

      try {
        await fetcher("/api/favourites/status", {
          method: "POST",
          body: JSON.stringify(
            buildFavouriteBody(true, influencerId, productId),
          ),
        });
        await refresh();
      } catch (err) {
        console.error(err);
      }
    },
    [influencerId, productId, refresh],
  );

  return (
    <>
      <button
        type="button"
        className={`${s.favBtn} ${variant === "light" ? s.light : s.dark}`}
        onClick={onToggle}
        disabled={isToggling}
        aria-label={
          isFavourite ? "Remove from favourites" : "Add to favourites"
        }
      >
        <HeartIcon pathClassName={isFavourite ? s.filled : s.outline} />
      </button>

      {showAuthModal && <AuthModal onClose={handleCloseAuthModal} />}
    </>
  );
}

FavouriteButton.propTypes = {
  productId: PropTypes.number,
  influencerId: PropTypes.number,
  variant: PropTypes.oneOf(["light", "dark"]),
};
